import React, { useState, useEffect, useRef } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Spinner } from './ui/spinner';
import { useNotifications } from '../context/NotificationContext';
import { useAgent } from '../context/AgentContext';
import { useBrand } from '../context/BrandContext';
import { useVipSource } from '../context/VipSourceContext';
import DashboardView from './vip/DashboardView';

const PAGE_SIZE = 100;

async function fetchAllVip(getDashboard, agent) {
  const first = await getDashboard(agent, { page: 1, page_size: PAGE_SIZE });
  const { data: firstPlayers, total, summary, latest_day } = first.data;

  if (!total || total <= firstPlayers.length) return first.data;

  const remaining = Math.ceil((total - firstPlayers.length) / PAGE_SIZE);
  const pages = await Promise.all(
    Array.from({ length: remaining }, (_, i) =>
      getDashboard(agent, { page: i + 2, page_size: PAGE_SIZE })
        .then(r => r.data.data || [])
    )
  );
  return { data: [...firstPlayers, ...pages.flat()], total, summary, latest_day };
}

function DataAgeLabel({ latestDay }) {
  if (!latestDay) return null;
  const raw = String(latestDay).slice(0, 10);
  const [ly, lm, ld] = raw.split('-').map(Number);
  const now = new Date();
  const diffDays = Math.floor(
    (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(ly, lm - 1, ld)) / 86400000
  );
  const color = diffDays > 2 ? 'text-negative' : 'text-positive';
  const label = diffDays === 0 ? 'today' : diffDays === 1 ? 'yesterday' : `${diffDays}d old`;
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-ink-dim">
      <div className="w-1.5 h-1.5 rounded-full shrink-0 bg-current" />
      <span>source: <span className="text-ink">{raw}</span></span>
      <span className={`uppercase ${color} tracking-widest`}>{label}</span>
    </div>
  );
}

export default function VipDashboardSection() {
  const { vipDataUpdate } = useNotifications();
  const { selectedAgent } = useAgent();
  const source = useVipSource();
  // The private dashboard's book is fixed on the server; fixedAgent only keys the loads.
  const agent = source.fixedAgent || selectedAgent;
  const { activeBrand } = useBrand();

  const [vipData, setVipData]       = useState(null);
  const [vipLoading, setVipLoading] = useState(false);
  const [vipError, setVipError]     = useState(null);

  // Each load claims a sequence number; a newer selection (agent or brand switch, flush reload)
  // supersedes an in-flight one, whose late response is discarded instead of overwriting the
  // newer brand's data.
  const loadSeq = useRef(0);

  const loadVip = async (selectedAgent) => {
    if (!selectedAgent) return;
    const seq = ++loadSeq.current;
    setVipLoading(true);
    setVipError(null);
    try {
      const data = await fetchAllVip(source.api.getDashboard, selectedAgent);
      if (seq !== loadSeq.current) return;
      setVipData(data);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setVipError(
        err.response?.status === 503
          ? 'Data source unavailable'
          : err.response?.data?.error || 'Failed to load VIP data'
      );
    } finally {
      if (seq === loadSeq.current) setVipLoading(false);
    }
  };

  useEffect(() => { loadVip(agent); }, [agent, activeBrand]); // eslint-disable-line react-hooks/exhaustive-deps
  // Reload on a data refresh only; agent and brand changes are handled by the effect above.
  // A flush names the brand it invalidated; only the dashboard viewing that brand reloads
  // (a message without a brand reloads everyone).
  useEffect(() => {
    if (!vipDataUpdate || !agent) return;
    if (vipDataUpdate.brand && vipDataUpdate.brand !== activeBrand) return;
    loadVip(agent);
  }, [vipDataUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">{source.title}</h2>
        <DataAgeLabel latestDay={vipData?.summary?.latest_day} />
      </div>

      {vipError && (
        <Alert variant="error" className="mb-4">
          <AlertDescription>{vipError}</AlertDescription>
        </Alert>
      )}
      {vipLoading && !vipData && (
        <div className="flex justify-center py-8"><Spinner size="lg" /></div>
      )}
      {vipData && (
        <div className="relative">
          {vipLoading && (
            <div className="absolute inset-0 flex items-start justify-center pt-8 z-10 bg-black/30 rounded-md">
              <Spinner />
            </div>
          )}
          <DashboardView rawData={vipData} agent={agent} />
        </div>
      )}
    </div>
  );
}
